<template>
  <div class="path-presenter" ref="selfElement">
    <template v-for="(entry, index) in vm.filteredList" :key="entry.id">
      <div
        class="path-element"
        :class="{ link: entry.click != undefined || (entry.id == '...' && dotsLinkStyle) }"
        ref="pathElements"
        :id="entry.id"
        :style="{ maxWidth: `${vm.goalWidth - 60}px` }"
        @click="elementClicked(entry)">
        <span class="path-element-icon" v-if="entry.icon && !ignoreIcons">
          <Icon :icon="entry.icon" />
        </span>
        <span class="path-element-text">
          {{ entry.name }}
        </span>
      </div>
      <div class="path-seperator" ref="pathSeperator" v-if="index != vm.filteredList.length - 1">
        <Icon icon="ChevronRight" size="14px" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { PathPresentedDataModel } from "@medapro-types/common/components/PathPresenter";
import _ from "lodash";
import { sha1 as hash } from "object-hash";
import { nextTick, reactive, ref, unref, watch, onMounted, onBeforeUnmount } from "vue";
import Icon from "./Icon.vue";

//#region General
const props = defineProps<{
  value?: PathPresentedDataModel[];
  reducedVisual?: boolean;
  noOverflowBehaviour?: boolean;
  withoutReducing?: boolean;
  dotsLinkStyle?: boolean;
  ignoreIcons?: boolean;
}>();
const emits = defineEmits(["elementClicked", "dotsClicked"]);
//#endregion

//#region Refs
const selfElement = ref<HTMLDivElement | null>(null);
const pathElements = ref<HTMLDivElement[] | null>(null);
const pathSeperator = ref<HTMLDivElement[] | null>(null);

let observer: ResizeObserver | null = null;
//#endregion

//#region Viewmodel
type ViewModel = { goalWidth: number; filteredList: PathPresentedDataModel[] };
const vm = reactive<ViewModel>({ goalWidth: 0, filteredList: [] });
//#endregion

//#region Interaction
const elementClicked = (entry: PathPresentedDataModel) => {
  if (entry.id == "...") emits("dotsClicked");
  else emits("elementClicked", entry);
  if (entry.click) {
    entry.click(entry);
  }
};
//#endregion

//#region Overflowing Handling
watch(
  () => props.value,
  (nv) => {
    let result = nv;

    if (result && result.length) {
      if (props.reducedVisual && result.length > 2) {
        const first = _.first(props.value);
        const last = _.last(props.value);
        if (first && last && first != last) result = [first, { id: "...", name: "..." }, last];
      }
      vm.filteredList = result;
    } else vm.filteredList = [];
  },
  { deep: true, immediate: true }
);

let pathElementsHash = hash(JSON.stringify(pathElements.value ? pathElements.value : ""));

watch(
  () => pathElements.value,
  (nv) => {
    const nvHash = hash(JSON.stringify(nv));
    if (nvHash != pathElementsHash) {
      pathElementsHash = nvHash;
      nextTick(reduceList);
    }
  },
  { deep: true }
);

const reduceList = () => {
  if (!props.withoutReducing && !props.reducedVisual && selfElement.value) {
    let list = _.cloneDeep(vm.filteredList);
    const parent = selfElement.value.parentElement;
    if (!parent) return;

    vm.goalWidth = parent.getBoundingClientRect().width;
    let currentWidth = selfElement.value.getBoundingClientRect().width;
    const _pathElements = unref(pathElements.value);
    if (_pathElements && currentWidth > vm.goalWidth) {
      list = list.filter((x) => !_.isEmpty(x.id));
      while (currentWidth > vm.goalWidth && list.length > 2) {
        const element = _pathElements.shift();
        if (!element) break;
        const elementWidth = element.getBoundingClientRect().width + 30;
        currentWidth = currentWidth - elementWidth;
        list.shift();
      }
      if (list.length > 1) list[0] = { id: "...", name: "..." };
    }
    vm.filteredList = list;
  }
};

const handleParentResize = _.throttle(() => {
  reduceList();
}, 200);
//#endregion

//#region LifeCycle
onMounted(() => {
  const parent = selfElement.value?.parentElement;
  if (parent && !props.noOverflowBehaviour) {
    observer = new ResizeObserver(() => {
      handleParentResize();
    });
    observer.observe(parent);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
//#endregion
</script>
